import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ServicesHeroSection = ({ onConsultationRequest }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  const translations = {
    en: {
      headline: "Complete ERP Solutions for Aluminum Excellence",
      subheadline: "End-to-end implementation, training, and support services designed specifically for aluminum extrusion manufacturers across India",
      primaryCTA: "Schedule Consultation",
      secondaryCTA: "View Service Packages",
      trustMetrics: {
        implementations: "500+ Successful Implementations",
        clientRetention: "99.2% Client Retention Rate",
        supportResponse: "24/7 Technical Support"
      }
    },
    hi: {
      headline: "एल्यूमीनियम उत्कृष्टता के लिए संपूर्ण ERP समाधान",
      subheadline: "भारत भर के एल्यूमीनियम एक्सट्रूज़न निर्माताओं के लिए विशेष रूप से डिज़ाइन की गई अंत-से-अंत कार्यान्वयन, प्रशिक्षण और सहायता सेवाएं",
      primaryCTA: "परामर्श निर्धारित करें",
      secondaryCTA: "सेवा पैकेज देखें",
      trustMetrics: {
        implementations: "500+ सफल कार्यान्वयन",
        clientRetention: "99.2% क्लाइंट रिटेंशन दर",
        supportResponse: "24/7 तकनीकी सहायता"
      }
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const handleViewPackages = () => {
    const packagesSection = document.getElementById('service-packages');
    if (packagesSection) {
      const headerHeight = 80;
      const targetPosition = packagesSection.offsetTop - headerHeight;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-primary-50 via-background to-surface-100 pt-20">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23000000%22 fill-opacity=%220.1%22%3E%3Ccircle cx=%227%22 cy=%227%22 r=%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content Column */}
          <div className="space-y-8">
            {/* Main Headline */}
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary leading-tight">
                {t.headline}
              </h1>
              
              <p className="text-lg md:text-xl text-text-secondary leading-relaxed max-w-2xl">
                {t.subheadline}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="primary"
                size="lg"
                onClick={onConsultationRequest}
                className="bg-gradient-accent hover:bg-accent-600 shadow-cta hover-scale"
                iconName="Calendar"
                iconPosition="left"
                iconSize={20}
              >
                {t.primaryCTA}
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                onClick={handleViewPackages}
                className="border-primary text-primary hover:bg-primary-50"
                iconName="Package"
                iconPosition="left"
                iconSize={20}
              >
                {t.secondaryCTA}
              </Button>
            </div>

            {/* Trust Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8">
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start space-x-2 mb-2">
                  <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                  <span className="text-2xl font-bold text-primary">500+</span>
                </div>
                <p className="text-sm text-text-secondary">{t.trustMetrics.implementations}</p>
              </div>
              
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start space-x-2 mb-2">
                  <Icon name="Award" size={20} color="var(--color-success)" />
                  <span className="text-2xl font-bold text-primary">99.2%</span>
                </div>
                <p className="text-sm text-text-secondary">{t.trustMetrics.clientRetention}</p>
              </div>
              
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start space-x-2 mb-2">
                  <Icon name="Clock" size={20} color="var(--color-primary)" />
                  <span className="text-2xl font-bold text-primary">24/7</span>
                </div>
                <p className="text-sm text-text-secondary">{t.trustMetrics.supportResponse}</p>
              </div>
            </div>
          </div>

          {/* Visual Column */}
          <div className="relative">
            <div className="relative bg-white rounded-2xl shadow-lg p-8 border border-border">
              {/* Mock Service Overview */}
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-primary">Service Overview</h3>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-success rounded-full"></div>
                    <span className="text-sm text-text-secondary">Active</span>
                  </div>
                </div>
                
                {/* Service Categories */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-surface-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-text-secondary">Implementation</span>
                      <Icon name="Settings" size={16} color="var(--color-primary)" />
                    </div>
                    <div className="text-2xl font-bold text-primary">12-16</div>
                    <div className="text-xs text-text-secondary">weeks timeline</div>
                  </div>
                  
                  <div className="bg-surface-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-text-secondary">Training</span>
                      <Icon name="BookOpen" size={16} color="var(--color-primary)" />
                    </div>
                    <div className="text-2xl font-bold text-primary">40+</div>
                    <div className="text-xs text-text-secondary">hours included</div>
                  </div>
                </div>
                
                {/* Service Progress */}
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-text-secondary">Support Coverage</span>
                      <span className="text-primary font-medium">100%</span>
                    </div>
                    <div className="w-full bg-surface-200 rounded-full h-2">
                      <div className="bg-gradient-accent h-2 rounded-full" style={{width: '100%'}}></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-text-secondary">Success Rate</span>
                      <span className="text-primary font-medium">99.2%</span>
                    </div>
                    <div className="w-full bg-surface-200 rounded-full h-2">
                      <div className="bg-gradient-primary h-2 rounded-full" style={{width: '99.2%'}}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 bg-success text-white rounded-full p-3 shadow-lg">
              <Icon name="CheckCircle" size={24} />
            </div>
            
            <div className="absolute -bottom-4 -left-4 bg-accent text-white rounded-full p-3 shadow-lg">
              <Icon name="Headphones" size={24} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesHeroSection;